export const CATEGORIES = [
  { key: 'service',     label: 'Service',     short: 'Svc', hasDue: true },
  { key: 'calibration', label: 'Calibration', short: 'Cal', hasDue: true },
  { key: 'repair',      label: 'Repair',      short: 'Rpr', hasDue: false },
  { key: 'warranty',    label: 'Warranty',    short: 'War', hasDue: false },
  { key: 'install',     label: 'Install',     short: 'Ins', hasDue: false },
];

const DUE_SOON_DAYS = 14;

export const CLOSED_SCHEDULE_STATUSES = [
  'Completed',
  'CompletedWithFurtherWork',
  'Cancelled',
  'Failed',
  'ReadyForInvoice',
  'Invoiced',
  'Closed',
];

export const EDITABLE_SCHEDULE_STATUSES = [
  'NotStarted',
];

const lockIdFieldFor = (category) => `active_${category}_schedule_id`;
const lockStatusFieldFor = (category) => `active_${category}_schedule_status`;

export function isCategoryLockActive(equipment, category) {
  if (!equipment || !category) return false;
  const lockId = equipment[lockIdFieldFor(category)];
  if (!lockId) return false;
  const status = equipment[lockStatusFieldFor(category)];
  if (status == null) return false;
  return !CLOSED_SCHEDULE_STATUSES.includes(status);
}

export function primaryActiveScheduleId(equipment) {
  if (!equipment) return null;
  for (const cat of CATEGORIES) {
    const id = equipment[lockIdFieldFor(cat.key)];
    if (id) return id;
  }
  return null;
}

export function isCategoryLockedElsewhere(equipment, category, currentScheduleId) {
  if (!equipment || !category) return false;
  const id = equipment[lockIdFieldFor(category)];
  if (!id) return false;
  if (id === currentScheduleId) return false;
  return isCategoryLockActive(equipment, category);
}

export function categoryExpiryInfo(equipment, category) {
  if (!equipment || !category) return null;
  let expiry = null;
  if (category === 'service') {
    expiry = equipment.latestServiceExpiry || equipment.next_service || null;
  } else if (category === 'calibration') {
    expiry = equipment.latestCalibrationExpiry || equipment.next_calibration || null;
  }
  if (!expiry || typeof expiry !== 'number' || expiry <= 0) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStartSec = Math.floor(today.getTime() / 1000);

  const expiryDay = new Date(expiry * 1000);
  expiryDay.setHours(0, 0, 0, 0);
  const expiryDaySec = Math.floor(expiryDay.getTime() / 1000);

  const diffDays = Math.round((expiryDaySec - todayStartSec) / 86400);
  return {
    date: expiry,
    overdue: diffDays < 0,
    daysOverdue: diffDays < 0 ? -diffDays : 0,
    dueSoon: diffDays >= 0 && diffDays <= DUE_SOON_DAYS,
    daysUntil: diffDays,
  };
}

function hasWorkStartedOnThisSchedule(scheduleDetail, currentScheduleId) {
  if (!scheduleDetail) return false;
  if (scheduleDetail.follow_up_id) return true;
  return !!currentScheduleId
    && scheduleDetail.schedule_id === currentScheduleId
    && !!scheduleDetail.job_sheet;
}

export function categoryPillState(equipment, category, currentScheduleId, scheduleDetail) {
  if (!equipment || !category) return 'free';

  const lockId = equipment[lockIdFieldFor(category)];
  const categoryOwnedByThisSchedule = !!lockId && lockId === currentScheduleId;

  if (categoryOwnedByThisSchedule) {
    const thisScheduleStatus = equipment[lockStatusFieldFor(category)];
    if (thisScheduleStatus && !EDITABLE_SCHEDULE_STATUSES.includes(thisScheduleStatus)) {
      return 'locked';
    }
    // The API refuses removal once a job sheet or follow-up exists for the
    // item on this schedule, so the pill locks for the same reasons.
    if (hasWorkStartedOnThisSchedule(scheduleDetail, currentScheduleId)) {
      return 'locked';
    }
    // While the schedule is still editable the pill follows the form's own
    // tick state, so an item unticked for removal no longer shows as added.
    // Only this schedule's own detail carries real flags; a default detail
    // (flags false, no schedule_id) still means the item is on the job.
    const untickedForRemoval = !!scheduleDetail
      && scheduleDetail.schedule_id === currentScheduleId
      && scheduleDetail[`is_${category}`] === false;
    if (!untickedForRemoval) return 'selected';
  }

  const activeLockElsewhere = lockId
    && lockId !== currentScheduleId
    && isCategoryLockActive(equipment, category);
  if (activeLockElsewhere) return 'booked';

  const flagOn = scheduleDetail && scheduleDetail[`is_${category}`];
  if (flagOn) return 'selected';

  if (category === 'service' || category === 'calibration') {
    const info = categoryExpiryInfo(equipment, category);
    if (info && (info.overdue || info.dueSoon)) return 'due';
  }

  return 'free';
}

export function rowBucket(equipment, scheduleDetail, currentScheduleId) {
  if (!equipment) return 'unscheduled';
  const isOperational = equipment.is_operational === true;
  const hasFollowUp = !!(scheduleDetail && scheduleDetail.follow_up_id);
  let hasSelected = false;
  let hasLocked = false;
  let hasBooked = false;
  let hasUpcomingDue = false;
  for (const cat of CATEGORIES) {
    const s = categoryPillState(equipment, cat.key, currentScheduleId, scheduleDetail);
    if (s === 'selected') hasSelected = true;
    else if (s === 'locked') hasLocked = true;
    else if (s === 'booked') hasBooked = true;
    else if (s === 'due' && isOperational) {
      const info = categoryExpiryInfo(equipment, cat.key);
      if (info && info.dueSoon) hasUpcomingDue = true;
    }
  }
  if (hasSelected || hasLocked) return 'on_this_schedule';
  if (hasFollowUp) return 'follow_up';
  if (hasUpcomingDue) return 'has_due_work';
  if (hasBooked) return 'booked_elsewhere';
  return 'unscheduled';
}

export function lockedScheduleJobId(equipment, category, scheduleDetail) {
  if (!scheduleDetail) return null;
  if (scheduleDetail.schedule_id === equipment[lockIdFieldFor(category)]) {
    return scheduleDetail.job_id || scheduleDetail.schedule_id;
  }
  return null;
}
